/**
 * pd-related.js — related products on a product page.
 *
 * The rule the merchant asked for: **if a bike is chosen, everything here fits it.** The old
 * widget sometimes offered parts that did not, which is worse than offering none. With no bike
 * chosen this is an ordinary related row and says nothing about fit.
 *
 * The row itself is the front page's card row. We render the same markup and hand it to
 * window.PdProductRow.mount, which owns the carousel, the quantity steppers and add-to-cart —
 * rather than keeping a second copy of all three in here.
 */
(function () {
  "use strict";

  var GARAGE_KEY = "fitment_garage";
  var GARAGE_ACTIVE_KEY = "fitment_garage_active";

  function activeVehicle() {
    try {
      var all = JSON.parse(localStorage.getItem(GARAGE_KEY) || "[]");
      if (!Array.isArray(all)) return null;
      var key = localStorage.getItem(GARAGE_ACTIVE_KEY) || "";
      if (!key) return null;
      return all.find(function (v) { return v.year + "|" + v.make + "|" + v.model === key; }) || null;
    } catch (e) {
      return null;
    }
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  var CHECK =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
    'stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';

  var CCY = null; // filled once market prices come back
  function money(amount, root) {
    if (window.pdFormatMoney && isFinite(Number(amount))) return window.pdFormatMoney(amount);   // the product page's format (theme.liquid)
    var n = Number(amount);
    if (!isFinite(n)) return "";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "da-DK", {
        style: "currency",
        currency: CCY || root.dataset.currency || "DKK",
      }).format(n);
    } catch (e) {
      return String(amount);
    }
  }

  // Same @inContext query the fairing kit and the wishlist use: products_cache holds the shop's
  // base amount, and printing that with the page's currency is wrong by the exchange rate.
  var SF_QUERY =
    "query P($ids:[ID!]!,$country:CountryCode!) @inContext(country:$country){ nodes(ids:$ids){ ... on ProductVariant { id price{ amount currencyCode } compareAtPrice{ amount currencyCode } } } }";

  async function priceInMarket(data, shop) {
    var ctxEl = document.querySelector("[data-pd-ctx]");
    var country = ctxEl && ctxEl.dataset.country;
    var rows = (data.items || []).filter(function (i) { return i.variantId; });
    if (!rows.length || !country || !data.storefrontToken) return null;
    var ids = [...new Set(rows.map(function (i) { return i.variantId; }))];
    var market = new Map();
    try {
      for (var i = 0; i < ids.length; i += 200) {
        var res = await fetch("https://" + shop + "/api/" + (data.storefrontApiVersion || "2025-01") + "/graphql.json", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": data.storefrontToken },
          body: JSON.stringify({
            query: SF_QUERY,
            variables: { ids: ids.slice(i, i + 200).map(function (v) { return "gid://shopify/ProductVariant/" + v; }), country: country },
          }),
        });
        if (!res.ok) continue;
        var body = await res.json();
        ((body && body.data && body.data.nodes) || []).forEach(function (n) {
          if (!n || !n.id || !n.price) return;
          var m = String(n.id).match(/(\d+)$/);
          if (m) market.set(m[1], {
            amount: Number(n.price.amount),
            currency: n.price.currencyCode,
            compareAt: n.compareAtPrice && n.compareAtPrice.amount != null ? Number(n.compareAtPrice.amount) : null,
          });
        });
      }
    } catch (e) {
      return null;
    }
    if (!market.size) return null;
    var ccy = null;
    rows.forEach(function (r) {
      var mk = market.get(String(r.variantId));
      if (!mk || mk.amount == null) return;
      r.price = mk.amount;
      r.compareAt = mk.compareAt;
      ccy = ccy || mk.currency;
    });
    return ccy;
  }

  // Shopify CDN images at card size (the cards are ~160-240px wide): the API hands out the original file.
  function cardImg(u) { if (!u || (u.indexOf("cdn.shopify.com") < 0 && u.indexOf("/cdn/shop/") < 0)) return u; return u + (u.indexOf("?") >= 0 ? "&" : "?") + "width=480"; }
  /** The feed's card, from our API's shape. Same classes as pd-product-row.js builds. */
  function card(p, L, root) {
    var url = p.handle ? "/products/" + p.handle : "#";
    var off = p.compareAt && p.compareAt > p.price ? Math.round((1 - p.price / p.compareAt) * 100) : 0;
    var art = p.image
      ? '<img src="' + esc(cardImg(p.image)) + '" alt="' + esc(p.title || "") + '" loading="lazy">'
      : '<div class="pd-thumb__art--placeholder"></div>';
    return (
      '<li class="pd-prow__item"><article class="pd-card pd-card--clean">' +
      '<a href="' + esc(url) + '" class="pd-thumb"><div class="pd-thumb__art">' + art + "</div>" +
      '<div class="pd-overlays"><span></span>' + (off > 0 ? '<span class="pd-sale">-' + off + "%</span>" : "<span></span>") + "</div>" +
      // Same chip as the feed card, and in the same place: on the image, bottom-left.
      (p.approval && p.approval.type || p.outlet
        ? '<div class="pd-tag-bar"><span class="pd-tag-row">' +
          (p.approval && p.approval.type
            ? '<span class="pd-tag__ece">' + CHECK + " " +
              esc(p.approval.type === "TEILEGUTACHTEN" ? "TÜV" : p.approval.type) + "</span>"
            : "") + "</span>" +
          (p.outlet
            ? '<span class="pd-outlet">' + esc(L.outlet) + (off > 0 ? " – " + esc(L.save) + " " + off + "%" : "") + "</span>"
            : "") + "</div>"
        : "") + "</a>" +
      '<div class="pd-card__body">' +
      '<a href="' + esc(url) + '"><h3 class="pd-name"></h3></a>' +
      '<span class="pd-sku"></span>' +
      // The stock line is filled from the live stock endpoint once the row is mounted (pd-product-row.js
      // liveStock): `p.available` is true for everything sold on backorder, so it said "På lager" on all of it.
      window.PdProductRow.stockLine(p.handle, p.available, L) +
      '<div class="pd-card__foot"><div class="pd-price">' +
      (off > 0 ? "<s>" + esc(money(p.compareAt, root)) + "</s>" : "") +
      "<strong>" + esc(money(p.price, root)) + "</strong></div></div>" +
      '<div class="pd-qty" data-pd-qty>' +
      '<button type="button" class="pd-qty__btn" data-pd-qty-dec aria-label="−">−</button>' +
      '<input class="pd-qty__in" type="number" inputmode="numeric" min="1" max="99" value="1">' +
      '<button type="button" class="pd-qty__btn" data-pd-qty-inc aria-label="+">+</button></div>' +
      '<button type="button" class="pd-add pd-add--block" data-pd-add="' + esc(p.variantId || "") + '"' +
      (p.available && p.variantId ? "" : " disabled") + "><span>" + esc(L.add) + "</span></button>" +
      "</div></article></li>"
    );
  }

  async function load(root) {
    var sku = root.dataset.sku;
    var shop = root.dataset.shop;
    if (!sku || !shop) return;

    var base = (root.dataset.apiBase || "https://pd-garage.fly.dev").replace(/\/$/, "");
    var url = new URL("/api/storefront/related-products", base);
    url.searchParams.set("shop", shop);
    url.searchParams.set("sku", sku);
    url.searchParams.set("limit", root.dataset.limit || "12");
    var bike = activeVehicle();
    if (bike) {
      url.searchParams.set("year", bike.year);
      url.searchParams.set("make", bike.make);
      url.searchParams.set("model", bike.model);
      if (bike.vehicleType) url.searchParams.set("vehicleType", bike.vehicleType);
    }

    var data;
    try {
      var res = await fetch(url.toString());
      if (!res.ok) return;
      data = await res.json();
    } catch (e) {
      return; // a missing row is better than a broken one
    }
    if (!data.items || !data.items.length) return;

    CCY = (await priceInMarket(data, shop)) || CCY;

    var L = {
      add: root.dataset.labelAdd || "Add to cart",
      inStock: root.dataset.labelInstock || "In stock",
      outStock: root.dataset.labelOutstock || "Out of stock",
      outlet: root.dataset.labelOutlet || "Outlet",
      save: root.dataset.labelSave || "save",
    };
    if (!window.PdProductRow || !window.PdProductRow.stockLine) return;   // the row script owns the cards' stock line and the carousel
    var track = root.querySelector("[data-pd-prow-track]");
    track.innerHTML = data.items.map(function (p) { return card(p, L, root); }).join("");
    // Titles and SKUs are merchant data: set as text, never interpolated into HTML.
    Array.prototype.forEach.call(track.children, function (li, i) {
      li.querySelector(".pd-name").textContent = data.items[i].title || "";
      li.querySelector(".pd-sku").textContent = data.items[i].sku || "";
    });

    // The pill claims a fit ONLY when the server actually filtered to the bike.
    var pill = root.querySelector("[data-rel-fits]");
    if (pill && data.fitted && data.vehicle) {
      // "2012 Harley Davidson Heritage Softail Classic FLSTC 1690" is longer than the pill
      // can ever be. Cut the NAME, not the sentence, so the label still reads as one.
      var name = String(data.vehicle);
      if (name.length > 30) name = name.slice(0, 29).replace(/[\s,–-]+$/, "") + "…";
      pill.textContent = (root.dataset.fitsLabel || "Fits your {vehicle}").replace("{vehicle}", name);
      pill.hidden = false;
    } else if (pill) {
      pill.hidden = true;
    }

    root.hidden = false;
    // Hand the finished row to the front page's row script: carousel, steppers, add to cart.
    if (window.PdProductRow && window.PdProductRow.mount) window.PdProductRow.mount(root);
  }

  function init() {
    document.querySelectorAll("[data-pd-related]").forEach(function (root) {
      if (root.dataset.relReady) return;
      root.dataset.relReady = "1";
      load(root);
    });
  }

  // Changing the bike changes what fits, so the row is rebuilt.
  window.addEventListener("fitment:garage-updated", function () {
    document.querySelectorAll("[data-pd-related]").forEach(function (root) {
      root.dataset.pdProwInit = ""; // let the row script bind the new cards
      delete root.dataset.relReady;
      root.dataset.relReady = "1";
      load(root);
    });
  });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
